// @ts-strict-ignore
import store from 'APP/Store'
import StartupActions from 'APP/Redux/StartupRedux'
import LoginActions from 'APP/Redux/LoginRedux'
import { preloadActions } from 'APP/Store/Preload'
import { openUrl } from 'APP/Services/Branch'
import { getNativeModule, isSupportedDeeplink } from './utils'
import {
  BROWNFIELD_BRIDGE_DEEPLINK_EVENT_TYPE,
  BROWNFIELD_BRIDGE_DEEPLINK_TYPE,
} from './deeplinkTypes'
import { deeplinkRouter } from './DeeplinkRouter'
import { waitForLocationUpdateGate } from './locationUpdateGate'

export async function triggerOpenUrl(params: any) {
  await waitForLocationUpdateGate()

  const { requestId, url } = params

  openUrl(url)

  const module = getNativeModule()

  module?.openUrlCallBackFor({
    type: BROWNFIELD_BRIDGE_DEEPLINK_EVENT_TYPE.DeeplinkOpened,
    payload: { requestId: requestId, callingUrl: url },
  })
}

export async function triggerOpenDeeplink(params: any) {
  await waitForLocationUpdateGate()

  const module = getNativeModule()

  if (!isSupportedDeeplink(params)) {
    module?.openDeeplinkCallbackFor({
      type: BROWNFIELD_BRIDGE_DEEPLINK_EVENT_TYPE.DeeplinkInvalid,
    })
    return
  }

  // All deeplink types go through Branch for uniform URL resolution.
  // The startup saga ensures Branch is subscribed before deeplinks arrive
  // and handles the race between Branch resolution and navigation.
  const url = deeplinkRouter.route(params)

  if (url && url.length > 0) {
    openUrl(url)

    if (params.type === BROWNFIELD_BRIDGE_DEEPLINK_TYPE.episode) {
      store.dispatch({
        type: 'FETCH_ONE_EPISODE_DEEPLINK_REQUEST',
        episodeId: params.payload.episodeId,
        requestId: params.requestId,
      })
    } else {
      module?.openDeeplinkCallbackFor({
        type: BROWNFIELD_BRIDGE_DEEPLINK_EVENT_TYPE.DeeplinkOpened,
        payload: { requestId: params.requestId },
      })
    }
  } else {
    module?.openDeeplinkCallbackFor({
      type: BROWNFIELD_BRIDGE_DEEPLINK_EVENT_TYPE.DeeplinkRejected,
      payload: { requestId: params.requestId },
    })
  }
}

function triggerPushTokenRegistration(
  params: { requestId: string; token: string },
  actionCreator: (token: string, retryCount: number) => any,
  successType: string,
  failureType: string,
  callbackMethod: string
) {
  const { requestId, token } = params
  if (!token) return

  const nativeModule = getNativeModule()

  try {
    const action = actionCreator(token, 5)
    store.dispatch(action)

    // For now, we'll call success immediately since the action
    // triggers an async saga that doesn't provide immediate feedback.
    // TODO: Hook into the saga completion or use a different approach for proper async handling
    nativeModule[callbackMethod]?.({
      type: successType,
      payload: { requestId },
    })
  } catch (error) {
    nativeModule[callbackMethod]?.({
      type: failureType,
      payload: {
        requestId,
        error: (error as Error).message,
      },
    })
  }
}

export function triggerRegisterPushToken(params: { requestId: string; token: string }) {
  triggerPushTokenRegistration(
    params,
    StartupActions.setPushTokenRequest,
    'REGISTER_PUSH_TOKEN_SUCCESS',
    'REGISTER_PUSH_TOKEN_FAILURE',
    'registerPushTokenCallbackFor'
  )
}

export function triggerRegisterVoipPushToken(params: { requestId: string; token: string }) {
  triggerPushTokenRegistration(
    params,
    StartupActions.setPushVoipTokenRequest,
    'REGISTER_VOIP_PUSH_TOKEN_SUCCESS',
    'REGISTER_VOIP_PUSH_TOKEN_FAILURE',
    'registerVoipPushTokenCallbackFor'
  )
}

export function triggerLogout(params: any) {
  const { requestId, reason } = params

  try {
    store.dispatch(LoginActions.logout(reason, true, requestId))
  } catch (error) {
    const nativeModule = getNativeModule()

    nativeModule?.logoutCallbackFor({
      type: 'LOGOUT_FAILURE',
      payload: {
        requestId,
        reason,
        error: (error as Error).message,
      },
    })
  }
}

export function triggerPreload() {
  store.dispatch(preloadActions.preload())
}

export function triggerResolveDeeplink(params: any) {
  const { requestId } = params

  if (typeof deeplinkRouter === 'undefined' || !deeplinkRouter) {
    return
  }

  const module = getNativeModule()

  try {
    const url = deeplinkRouter.route(params)

    if (url && url.length > 0) {
      module?.resolveDeeplinkCallbackFor({
        type: 'RESOLVE_DEEPLINK_SUCCESS',
        payload: {
          requestId: requestId,
          url: url,
        },
      })
    } else {
      module?.resolveDeeplinkCallbackFor({
        type: 'RESOLVE_DEEPLINK_FAILURE',
        payload: {
          requestId: requestId,
          url: null,
        },
      })
    }
  } catch (_e) {
    module?.resolveDeeplinkCallbackFor({
      type: 'RESOLVE_DEEPLINK_FAILURE',
      payload: {
        requestId: requestId,
        url: null,
      },
    })
  }
}
